/* eslint-disable no-param-reassign */
import routeListService from '@/modules/Flexigo/services/routeList';
import routeService from '@/modules/Flexigo/services/route';

const normalizeRouteId = (routeId) => {
  if (routeId === null || routeId === undefined || routeId === '') return null;
  const asNumber = Number(routeId);
  return Number.isNaN(asNumber) ? null : asNumber;
};

const initialState = {
  assignedData: null,
  routeDetails: null,
  routeAssignmentSchedule: [],
  isLoadingRouteDetails: false,
  isLoadingRouteAssignmentSchedule: false,
  error: null,
  // Monotonic token that identifies the latest in-flight request set.
  // Responses whose token does not match this value are discarded to avoid
  // stale data overwriting newer state (e.g. when panels are opened/closed quickly).
  requestToken: 0,
};

const state = () => ({ ...initialState });

const withNormalizedRouteId = (payload) => {
  if (!payload) return payload;
  if (!('routeId' in payload)) return payload;
  return { ...payload, routeId: normalizeRouteId(payload.routeId) };
};

const mutations = {
  SET_ASSIGNED_DATA(storeState, payload) {
    storeState.assignedData = withNormalizedRouteId(payload);
  },
  PATCH_ASSIGNED_DATA(storeState, patch) {
    storeState.assignedData = withNormalizedRouteId({
      ...(storeState.assignedData || {}),
      ...(patch || {}),
    });
  },
  SET_ROUTE_DETAILS(storeState, payload) {
    storeState.routeDetails = payload;
  },
  SET_ROUTE_ASSIGNMENT_SCHEDULE(storeState, payload) {
    storeState.routeAssignmentSchedule = payload;
  },
  SET_IS_LOADING_ROUTE_DETAILS(storeState, payload) {
    storeState.isLoadingRouteDetails = payload;
  },
  SET_IS_LOADING_ROUTE_ASSIGNMENT_SCHEDULE(storeState, payload) {
    storeState.isLoadingRouteAssignmentSchedule = payload;
  },
  SET_ERROR(storeState, payload) {
    storeState.error = payload;
  },
  CLEAR_ERROR(storeState) {
    storeState.error = null;
  },
  BUMP_REQUEST_TOKEN(storeState) {
    storeState.requestToken += 1;
  },
  CLEAR_ASSIGNED_DATA(storeState) {
    storeState.assignedData = null;
    storeState.routeDetails = null;
    storeState.routeAssignmentSchedule = [];
    storeState.isLoadingRouteDetails = false;
    storeState.isLoadingRouteAssignmentSchedule = false;
    storeState.error = null;
  },
  RESET(storeState) {
    Object.assign(storeState, {
      assignedData: null,
      routeDetails: null,
      routeAssignmentSchedule: [],
      isLoadingRouteDetails: false,
      isLoadingRouteAssignmentSchedule: false,
      error: null,
      requestToken: 0,
    });
  },
};

const normalizeFetchArgs = (commit, storeState, arg) => {
  // Accept either a primitive routeId (legacy callers) or an object { routeId, token }.
  if (arg && typeof arg === 'object' && !Array.isArray(arg)) {
    return { routeId: arg.routeId, token: arg.token };
  }
  // Legacy caller: bump the token so its response wins over any earlier in-flight request.
  commit('BUMP_REQUEST_TOKEN');
  return { routeId: arg, token: storeState.requestToken };
};

const actions = {
  async fetchRouteDetails({ commit, state: storeState }, arg) {
    const { routeId, token } = normalizeFetchArgs(commit, storeState, arg);
    if (!routeId) {
      if (token === storeState.requestToken) commit('SET_ROUTE_DETAILS', null);
      return null;
    }
    commit('SET_IS_LOADING_ROUTE_DETAILS', true);
    try {
      const response = await routeListService.getRouteDetails(routeId);
      // Discard stale responses
      if (token !== storeState.requestToken) return null;
      const routeDetails = response && typeof response === 'object' ? response : null;
      commit('SET_ROUTE_DETAILS', routeDetails);
      return routeDetails;
    } catch (error) {
      if (token !== storeState.requestToken) return null;
      console.error('Error fetching route details:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch route details');
      commit('SET_ROUTE_DETAILS', null);
      throw error;
    } finally {
      if (token === storeState.requestToken) {
        commit('SET_IS_LOADING_ROUTE_DETAILS', false);
      }
    }
  },
  async fetchRouteAssignmentSchedule({ commit, state: storeState }, arg) {
    const { routeId, token } = normalizeFetchArgs(commit, storeState, arg);
    if (!routeId) {
      if (token === storeState.requestToken) commit('SET_ROUTE_ASSIGNMENT_SCHEDULE', []);
      return [];
    }
    commit('SET_IS_LOADING_ROUTE_ASSIGNMENT_SCHEDULE', true);
    try {
      const response = await routeService.getDriverVehicleMatch(routeId);
      // Discard stale responses
      if (token !== storeState.requestToken) return [];
      const schedule = Array.isArray(response) ? response : [];
      commit('SET_ROUTE_ASSIGNMENT_SCHEDULE', schedule);
      return schedule;
    } catch (error) {
      if (token !== storeState.requestToken) return [];
      console.error('Error fetching route assignment schedule:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch route assignment schedule');
      commit('SET_ROUTE_ASSIGNMENT_SCHEDULE', []);
      throw error;
    } finally {
      if (token === storeState.requestToken) {
        commit('SET_IS_LOADING_ROUTE_ASSIGNMENT_SCHEDULE', false);
      }
    }
  },
  async setAssignedData({ commit, dispatch, state: storeState }, payload) {
    commit('SET_ASSIGNED_DATA', payload || null);
    // Invalidate any in-flight responses from previous dispatches
    commit('BUMP_REQUEST_TOKEN');
    commit('CLEAR_ERROR');
    const { requestToken: token } = storeState;
    const routeId = normalizeRouteId(payload && payload.routeId);
    const requests = [];
    if (routeId) {
      requests.push(dispatch('fetchRouteDetails', { routeId, token }).catch(() => null));
      requests.push(dispatch('fetchRouteAssignmentSchedule', { routeId, token }).catch(() => null));
    } else {
      commit('SET_ROUTE_DETAILS', null);
      commit('SET_ROUTE_ASSIGNMENT_SCHEDULE', []);
    }
    await Promise.all(requests);
    return payload || null;
  },
  async patchAssignedData({ commit, dispatch, state: storeState }, patch) {
    const previousRouteId = normalizeRouteId(storeState.assignedData && storeState.assignedData.routeId);
    commit('PATCH_ASSIGNED_DATA', patch);
    const nextRouteId = normalizeRouteId(storeState.assignedData && storeState.assignedData.routeId);
    // If routeId changed as part of the patch, refresh the dependent fetches with a fresh token
    if (nextRouteId !== previousRouteId) {
      commit('BUMP_REQUEST_TOKEN');
      commit('CLEAR_ERROR');
      const { requestToken: token } = storeState;
      if (nextRouteId) {
        await Promise.all([
          dispatch('fetchRouteDetails', { routeId: nextRouteId, token }).catch(() => null),
          dispatch('fetchRouteAssignmentSchedule', { routeId: nextRouteId, token }).catch(() => null),
        ]);
      } else {
        commit('SET_ROUTE_DETAILS', null);
        commit('SET_ROUTE_ASSIGNMENT_SCHEDULE', []);
      }
    }
  },
  clearAssignedData({ commit }) {
    // Invalidate any in-flight fetches so late responses don't repopulate cleared state
    commit('BUMP_REQUEST_TOKEN');
    commit('CLEAR_ASSIGNED_DATA');
  },
  reset({ commit }) {
    commit('RESET');
  },
};

const getters = {
  assignedData: (storeState) => storeState.assignedData,
  routeId: (storeState) => normalizeRouteId(storeState.assignedData && storeState.assignedData.routeId),
  ringId: (storeState) => (storeState.assignedData ? storeState.assignedData.ringId : null) || null,
  routeDetails: (storeState) => storeState.routeDetails,
  routeAssignmentSchedule: (storeState) => storeState.routeAssignmentSchedule,
  isLoadingRouteDetails: (storeState) => storeState.isLoadingRouteDetails,
  isLoadingRouteAssignmentSchedule: (storeState) => storeState.isLoadingRouteAssignmentSchedule,
  error: (storeState) => storeState.error,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
/* eslint-enable no-param-reassign */
